/**
 * RF-09 — Gestão de contas bancárias por departamento, forma de pagamento e
 * funcionário. Implementa UI-09-01 (listagem), UI-09-02 (adicionar),
 * UI-09-03 (editar) e UI-09-04 (apagar — eliminação lógica).
 *
 * Spec:    specs/02_features/RF-09-contas-bancarias.md
 * UI:      specs/04_ux_ui/web/ui-09-01-contas-bancarias.md
 * Modelo:  codebase/common/models/CoreDepartamentoPagamentoConta.json
 *
 * Componente registado em AngularJS via react2angular em
 * `codebase/client/src/modules/administration/administration/index.js`.
 * Os serviços LoopBack chegam como props (já com autenticação resolvida).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Fab, FormControl, IconButton, InputLabel, MenuItem, Select, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { mdiDelete, mdiPencil, mdiPlus } from '@mdi/js';
import { MdiIcon, Table } from '@components';
// ── Helpers ──────────────────────────────────────────────────────────────────
const stripAccents = (s) => s.normalize('NFD').replace(/[̀-ͯ]/g, '');
/**
 * RB defensiva (ver open-questions): identifica uma forma de pagamento como
 * "numerário" quando o `codigo` é "NUM" ou a `designacao` contém "numerario".
 */
const ehNumerario = (fp) => {
    if (!fp)
        return false;
    if ((fp.codigo || '').toUpperCase() === 'NUM')
        return true;
    return stripAccents((fp.designacao || '').toLowerCase()).includes('numerario');
};
// ── Componente principal ────────────────────────────────────────────────────
const ContasBancariasListView = ({ $state, AdministrationService, UIService, AuthorizationService }) => {
    // Adicionar, editar e desativar só para quem tem o perfil de edição.
    const podeEditar = AuthorizationService.canPerform('coreEditarDepartamentoContasBancarias');
    const [data, setData] = useState();
    const [isLoading, setLoading] = useState(true);
    const [refetching, setRefetching] = useState(false);
    const [departamentos, setDepartamentos] = useState([]);
    const [formasPagamento, setFormasPagamento] = useState([]);
    const [funcionarios, setFuncionarios] = useState([]);
    // Estado dos diálogos.
    const [dialog, setDialog] = useState('none');
    const [edit, setEdit] = useState(null);
    const [searchMode, setSearchMode] = useState('todos');
    const loadParams = () => {
        const sortBy = $state.params.sortBy;
        if (sortBy == undefined) {
            return [];
        }
        const o = [];
        sortBy.forEach((r) => {
            const s = r.split(':');
            o.push({ id: s[0], desc: s[1] == 'DESC' });
        });
        return o;
    };
    const getType = (type) => {
        switch (type) {
            case 'contains':
                return '$ilike';
            case 'startsWith':
                return '$sw';
            case 'notEquals':
                return '$not';
            case 'empty':
                return '$null';
            case 'notEmpty':
                return '$notNull';
            case 'greaterThan':
                return '$gt';
            case 'greaterThanOrEqualTo':
                return '$gte';
            case 'lessThan':
                return '$lt';
            case 'lessThanOrEqualTo':
                return '$lte';
            default:
                return '$eq';
        }
    };
    const [options, setOptions] = useState({
        filter: [],
        sort: []
    });
    const [filter, setFilter] = useState([]);
    const [sort, setSort] = useState(() => loadParams());
    const initialTableState = useMemo(() => ({
        sort,
        filter
    }), [sort, filter]);
    const [pagination, setPagination] = useState({
        pageIndex: $state.params.page - 1 || 0,
        pageSize: $state.params.limit || 20
    });
    // ── Carregamento ───────────────────────────────────────────────────────────
    const initialFetch = async () => {
        try {
            const opt = await AdministrationService.pagamentoContaOptions();
            setOptions(opt);
        }
        catch (e) {
            UIService.addToast('Erro a carregar opções: ' + (e?.data?.error?.message || e?.message || ''));
        }
    };
    const fetchData = async () => {
        try {
            setLoading(true);
            let filters = {};
            filter.forEach((r) => {
                filters[r.id] = `${getType(r.type)}:${r.value}`;
            });
            if (searchMode === 'colaborador') {
                filters.funcionarioId = '$not:null';
            }
            else if (searchMode === 'departamento') {
                filters.funcionarioId = '$null:';
            }
            let sorter = [];
            sort.forEach((r) => {
                sorter.push(`${r.id}:${r.desc ? 'DESC' : 'ASC'}`);
            });
            const result = await AdministrationService.pagamentoContaTable({
                limit: pagination.pageSize,
                page: pagination.pageIndex + 1,
                filter: filters,
                sortBy: sorter
            });
            setData(result);
        }
        catch (e) {
            UIService.addToast('Erro a carregar contas: ' + (e?.data?.error?.message || e?.message || ''));
        }
        finally {
            setLoading(false);
            setRefetching(false);
        }
    };
    useEffect(() => {
        // Lookups uma única vez no mount.
        (async () => {
            try {
                const [deps, fps, funcs] = await AdministrationService.listasApoioFind();
                setDepartamentos(deps);
                setFormasPagamento(fps);
                setFuncionarios(funcs);
            }
            catch (e) {
                UIService.addToast('Erro a carregar listas de apoio: ' + (e?.data?.error?.message || e?.message || ''));
            }
        })();
    }, [AdministrationService, UIService]);
    useEffect(() => {
        initialFetch();
    }, []);
    useEffect(() => {
        if (!refetching) {
            setRefetching(true);
            fetchData();
        }
    }, [pagination, filter, sort, searchMode]);
    // ── Tabela (UI-09-01) ──────────────────────────────────────────────────────
    const columns = useMemo(() => [
        { accessorKey: 'Departamento.designacao', header: 'Departamento', size: 200 },
        { accessorKey: 'FormaPagamento.designacao', header: 'Forma de pagamento', size: 200 },
        {
            accessorKey: 'Funcionario.name',
            header: 'Funcionário',
            size: 220,
            Cell: ({ row }) => row.original?.Funcionario?.name
        },
        { accessorKey: 'conta', header: 'Conta', size: 300 }
    ], []);
    const onAdd = () => {
        setEdit({
            id: 0,
            departamentoId: null,
            formaPagamentoId: null,
            funcionarioId: null,
            conta: ''
        });
        setDialog('add');
    };
    const onEdit = (row) => {
        setEdit({ ...row.row.original });
        setDialog('edit');
    };
    const onDelete = (row) => {
        setEdit({ ...row.row.original });
        setDialog('delete');
    };
    // ── Render ─────────────────────────────────────────────────────────────────
    return (<Box sx={{ p: 2 }}>
      <Typography variant='h4' gutterBottom>
        Gestão de contas
      </Typography>
      <Typography variant='subtitle2' color='text.secondary' gutterBottom>
        Parametrização › Faturação automática
      </Typography>
      <Stack direction='row' spacing={1} sx={{ mb: 2 }}>
        {[
            { key: 'todos', label: 'Todos' },
            { key: 'departamento', label: 'Departamento' },
            { key: 'colaborador', label: 'Colaborador' }
        ].map(item => (<Button key={item.key} onClick={() => setSearchMode(item.key)} sx={{
                borderRadius: 20,
                px: 3,
                backgroundColor: searchMode === item.key ? '#049a9b' : 'transparent',
                color: searchMode === item.key ? '#fff' : '#049a9b',
                border: '1px solid #049a9b',
                '&:hover': {
                    backgroundColor: searchMode === item.key ? '#038485' : 'rgba(4,154,155,0.08)'
                }
            }}>
            {item.label}
          </Button>))}
      </Stack>
      {podeEditar && (<Fab aria-label='add' sx={{
                position: 'fixed',
                bottom: 16,
                right: 16,
                zIndex: 1000,
                backgroundColor: '#d32f2f',
                color: '#fff',
                '&:hover': {
                    backgroundColor: '#b71c1c'
                }
            }} onClick={onAdd}>
          <MdiIcon path={mdiPlus} sx={{ color: '#ffffff' }}/>
        </Fab>)}

      <Table storageKey='rf09-contas-bancarias' data={data} columns={columns} initial={initialTableState} isLoading={isLoading} options={options} onPaginate={setPagination} onFilter={setFilter} onSort={setSort} actionItems={({ row }) => !podeEditar ? [] : [
            <Tooltip key='edit' title='Editar'>
            <IconButton size='small' onClick={() => onEdit({ row })}>
              <MdiIcon path={mdiPencil} size={1}/>
            </IconButton>
          </Tooltip>,
            <Tooltip key='del' title='Desativar'>
            <IconButton size='small' onClick={() => onDelete({ row })}>
              <MdiIcon path={mdiDelete} size={1}/>
            </IconButton>
          </Tooltip>
        ]}/>
      {/* UI-09-02 / UI-09-03 — Adicionar / Editar */}
      {(dialog === 'add' || dialog === 'edit') && edit && (<FormDialog mode={dialog} value={edit} onChange={setEdit} departamentos={departamentos} formasPagamento={formasPagamento} funcionarios={funcionarios} existentes={data?.data || []} onCancel={() => {
                setDialog('none');
                setEdit(null);
            }} onSave={async () => {
                try {
                    let conflitoServer;
                    if (edit.funcionarioId != null) {
                        conflitoServer = await AdministrationService.pagamentoContaTable({
                            limit: 10,
                            page: 1,
                            filter: {
                                departamentoId: '$eq:6',
                                funcionarioId: `$eq:${edit.funcionarioId}`,
                                active: '$eq:1'
                            }
                        });
                    }
                    else {
                        const conflitoServer = await AdministrationService.pagamentoContaTable({
                            limit: 10,
                            page: 1,
                            filter: {
                                departamentoId: `$eq:${edit.departamentoId}`,
                                active: '$eq:1'
                            }
                        });
                        if (conflitoServer) {
                            const conflitos = conflitoServer.data.filter((r) => r.funcionarioId == null && r.id !== edit.id);
                            if (conflitos.length > 0) {
                                throw new Error('Este departamento já possui uma conta.');
                            }
                        }
                    }
                    if (conflitoServer && conflitoServer.data.length > 0) {
                        if (edit.funcionarioId) {
                            UIService.addToast('Erro a gravar: Utilizador já tem conta criada');
                        }
                        else {
                            UIService.addToast('Erro a gravar: Já existe conta para este departamento');
                        }
                        return;
                    }
                    await AdministrationService.upsertDepartamentoPagamentoConta({
                        ...edit,
                        departamentoId: edit.funcionarioId ? 6 : edit.departamentoId,
                        formaPagamentoId: edit.funcionarioId ? 1 : edit.formaPagamentoId
                    });
                    UIService.addToast('Registo gravado.');
                    setDialog('none');
                    setEdit(null);
                    await fetchData();
                }
                catch (e) {
                    UIService.addToast('Erro a gravar: ' + (e?.data?.error?.message || e?.message || ''));
                }
            }}/>)}

      {/* UI-09-04 — Apagar (eliminação lógica) */}
      {dialog === 'delete' && edit && (<DeleteDialog row={edit} onCancel={() => {
                setDialog('none');
                setEdit(null);
            }} onConfirm={async () => {
                try {
                    await AdministrationService.upsertDepartamentoPagamentoConta({ ...edit, active: 0 });
                    UIService.addToast('Registo desativado.');
                    setDialog('none');
                    setEdit(null);
                    await fetchData();
                }
                catch (e) {
                    UIService.addToast('Erro a desativar: ' + (e?.data?.error?.message || e?.message || ''));
                }
            }}/>)}
    </Box>);
};
const FormDialog = ({ mode, value, onChange, departamentos, formasPagamento, funcionarios, existentes, onCancel, onSave }) => {
    const fpSelecionada = formasPagamento.find((fp) => fp.id === value.formaPagamentoId);
    // Validações UI-09 §Validações + RB-004 (cliente-side).
    const erros = useMemo(() => {
        const e = {};
        if (!value.departamentoId)
            e.departamentoId = 'Departamento é obrigatório.';
        if (!value.formaPagamentoId)
            e.formaPagamentoId = 'Forma de pagamento é obrigatória.';
        if (!value.conta || !value.conta.trim())
            e.conta = 'Conta é obrigatória.';
        // RB-004 — unicidade do tuplo (cliente-side; índice no BD complementa).
        // Um funcionário só pode ter UMA conta ativa por departamento
        const contaExistente = existentes.find((r) => {
            if (r.id === value.id || r.active === 0) {
                return false;
            }
            // Gás
            if (value.funcionarioId != null) {
                return r.departamentoId === 6 && r.funcionarioId === value.funcionarioId;
            }
            // Restantes departamentos
            return r.funcionarioId == null && r.departamentoId === value.departamentoId;
        });
        if (contaExistente) {
            if (value.funcionarioId != null) {
                e.funcionarioId = 'Este funcionário já possui uma conta.';
            }
            else {
                e.departamentoId = 'Este departamento já possui uma conta.';
            }
        }
        return e;
    }, [value, existentes]);
    const podeGravar = Object.keys(erros).length === 0;
    return (<Dialog open onClose={onCancel} maxWidth='sm' fullWidth>
      <DialogTitle>{mode === 'add' ? 'Adicionar conta' : 'Editar conta'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth error={!!erros.funcionarioId}>
            <InputLabel>Funcionário</InputLabel>

            <Select value={value.funcionarioId ?? ''} label='Funcionário' disabled={mode !== 'add'} onChange={ev => {
            const funcionarioId = ev.target.value === '' ? null : Number(ev.target.value);
            onChange({
                ...value,
                funcionarioId,
                departamentoId: funcionarioId ? 6 : value.departamentoId,
                formaPagamentoId: funcionarioId ? 1 : value.formaPagamentoId
            });
        }}>
              <MenuItem value=''>
                <em>Selecionar...</em>
              </MenuItem>

              {funcionarios
            .filter((f) => {
            const temContaAtiva = existentes.some((r) => r.active !== 0 && r.funcionarioId === f.id && r.id !== value.id);
            return !temContaAtiva;
        })
            .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt', {
            sensitivity: 'base'
        }))
            .map((f) => (<MenuItem key={f.id} value={f.id}>
                    {f.name || `#${f.id}`}
                  </MenuItem>))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Departamento</InputLabel>

            <Select value={value.departamentoId ?? ''} disabled={value.funcionarioId != null} label='Departamento' onChange={ev => onChange({
            ...value,
            departamentoId: Number(ev.target.value) || null
        })}>
              {departamentos
            .filter((d) => {
            // Quando há funcionário selecionado não interessa,
            // porque o departamento fica automaticamente 6
            if (value.funcionarioId != null) {
                return true;
            }
            const temContaAtiva = existentes.some((r) => r.active !== 0 &&
                r.funcionarioId == null && // só contas de departamento
                r.departamentoId === d.id &&
                r.id !== value.id // permite editar o próprio registo
            );
            return !temContaAtiva;
        })
            .sort((a, b) => (a.designacao || '').localeCompare(b.designacao || '', 'pt', { sensitivity: 'base' }))
            .map((d) => (<MenuItem key={d.id} value={d.id}>
                    {d.designacao}
                  </MenuItem>))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Forma de pagamento</InputLabel>

            <Select value={value.formaPagamentoId ?? ''} disabled={value.funcionarioId != null} label='Forma de pagamento' onChange={ev => onChange({
            ...value,
            formaPagamentoId: Number(ev.target.value) || null
        })}>
              {formasPagamento.map((fp) => (<MenuItem key={fp.id} value={fp.id}>
                  {fp.designacao}
                </MenuItem>))}
            </Select>
          </FormControl>
          {!!erros.funcionarioId && (<Typography variant='caption' color='error' sx={{ mt: 0.5 }}>
              {erros.funcionarioId}
            </Typography>)}
          <TextField label='Conta bancária' required fullWidth value={value.conta || ''} onChange={ev => onChange({ ...value, conta: ev.target.value })} error={!!erros.conta} helperText={erros.conta}/>

          {erros._dup && (<Typography variant='caption' color='error'>
              {erros._dup}
            </Typography>)}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancelar</Button>
        <Button variant='contained' disabled={!podeGravar} onClick={onSave}>
          Gravar
        </Button>
      </DialogActions>
    </Dialog>);
};
// ── UI-09-04 — Apagar (confirmação) ─────────────────────────────────────────
const DeleteDialog = ({ row, onCancel, onConfirm }) => {
    const descricao = row.conta;
    return (<Dialog open onClose={onCancel} maxWidth='xs' fullWidth>
      <DialogTitle>Tem a certeza que quer apagar este registo?</DialogTitle>
      <DialogContent>
        <Typography variant='body2'>{descricao}</Typography>
      </DialogContent>
      <DialogActions sx={{ display: 'flex', justifyContent: 'space-between', px: 3, pb: 2 }}>
        <Button variant='text' onClick={onCancel}>
          Cancelar
        </Button>
        <Button variant='contained' color='error' onClick={onConfirm}>
          Apagar
        </Button>
      </DialogActions>
    </Dialog>);
};
export default ContasBancariasListView;
